import { Checkbox } from '@kcftech/design-system';
import { ValidatableLabel } from 'components/forms/ValidatableLabel';
import { useUser } from 'contexts/UserContext';
import { FaultTypesInput } from 'pages/Desk/issues/fault_type/FaultTypesInput';
import { isKcfUser } from 'pages/Desk/issues/shared';
import { CreateNotificationSubscriptionFormInputProps } from 'pages/Settings/NotificationSubscriptions/form/types';
import { useRef, useState } from 'react';
import { Controller, useWatch } from 'react-hook-form';
import { ConfirmActionPopup } from 'shared/triggered_popup/ConfirmActionPopup';
import { toHumanReadable } from 'utils/array';

export function HardwareIssuesNotificationSubscriptionFormContent({
    form,
    setSelectedUsers,
}: CreateNotificationSubscriptionFormInputProps) {
    const { user } = useUser();

    const {
        control,
        setValue,
        formState: { errors },
    } = form;

    const popupRef = useRef<HTMLDivElement>(null);
    const triggerRef = useRef<HTMLDivElement>(null);

    const [invalidData, setInvalidData] = useState<{
        type: 'users' | 'fault-types';
        ids: string[];
        message: string;
    }>();

    const allowInternalOnly = useWatch({
        control,
        name: 'hardwareIssues.allowInternalOnly',
    });

    const faultTypes = useWatch({
        control,
        name: 'hardwareIssues.faultTypes',
    });

    const users = useWatch({
        control,
        name: 'users',
    });

    const toggleAllowInternalOnly = (newValue: boolean) => {
        // If we're going to allow internal only, check for external KCF users
        if (newValue) {
            const invalidUsers = users.filter(
                (user) => !isKcfUser(user.ApplicationRoleCode)
            );

            if (invalidUsers.length > 0) {
                setInvalidData({
                    type: 'users',
                    ids: invalidUsers.map((user) => user.Id),
                    message: toHumanReadable(
                        invalidUsers,
                        (user) => `${user.FirstName} ${user.LastName}`
                    ),
                });
                return;
            }
        }

        // If we're going to disable internal only, check for internal only fault types
        const invalidFaultTypes = faultTypes.filter((type) => type.internalOnly);
        if (invalidFaultTypes.length > 0) {
            setInvalidData({
                type: 'fault-types',
                ids: invalidFaultTypes.map((type) => type.id),
                message: toHumanReadable(invalidFaultTypes, (type) => type.fullName),
            });
            return;
        }

        setInvalidData(undefined);
        setValue('hardwareIssues.allowInternalOnly', newValue);
    };

    const clearInvalidFormData = () => {
        if (invalidData?.type == 'users') {
            setSelectedUsers(users.filter((user) => !invalidData.ids.includes(user.Id)));
        }

        if (invalidData?.type == 'fault-types') {
            setValue(
                'hardwareIssues.faultTypes',
                faultTypes.filter((type) => !invalidData.ids.includes(type.id))
            );
        }

        setValue('hardwareIssues.allowInternalOnly', !allowInternalOnly);
        setInvalidData(undefined);
    };

    return (
        <>
            <div ref={triggerRef}>
                {user && isKcfUser(user.ApplicationRole) && (
                    <Checkbox
                        label="Allow KCF Only"
                        checked={allowInternalOnly}
                        onChange={toggleAllowInternalOnly}
                    />
                )}
            </div>
            <ValidatableLabel
                label="Fault Type Notifications"
                required
                error={errors.hardwareIssues?.faultTypes}
            >
                <Controller
                    name="hardwareIssues.faultTypes"
                    control={control}
                    render={({ field: { value, onChange } }) => (
                        <FaultTypesInput
                            issueMode="hardwareHealth"
                            faultTypeIds={value.map((item) => item.id)}
                            onChange={(event) => onChange(event)}
                            hideInternal={!allowInternalOnly}
                        />
                    )}
                />
            </ValidatableLabel>
            {invalidData && (
                <ConfirmActionPopup
                    destructive
                    anchorRef={triggerRef}
                    ref={popupRef}
                    test-id="confirm-allow-internal-only-toggle"
                    onNo={() => setInvalidData(undefined)}
                    onYes={clearInvalidFormData}
                    noButtonLabel="Cancel"
                    yesButtonLabel="Confirm"
                >
                    <div className="flex-column">
                        <span>
                            Are you sure you want to{' '}
                            {allowInternalOnly ? 'disable' : 'enable'} internal only?
                        </span>
                        <span>
                            This action will result in the following values being cleared:
                        </span>
                        <strong className="mt-10">{invalidData.message}</strong>
                    </div>
                </ConfirmActionPopup>
            )}
        </>
    );
}
